// "Share" turns a story into a branded 1080×1350 image (Instagram/TikTok/
// WhatsApp size) drawn in the browser with <canvas>: no server, no cost.
// On phones it opens the native share sheet; on computers it downloads the PNG.
// Every shared image carries newsnetwide.com, so each share is free advertising.

import { t, lang } from '../i18n/index.js';

const W = 1080;
const H = 1350;

function wrap(ctx, text, maxWidth) {
    const words = String(text || '').split(/\s+/);
    const lines = [];
    let line = '';
    for (const w of words) {
        const test = line ? `${line} ${w}` : w;
        if (ctx.measureText(test).width > maxWidth && line) {
            lines.push(line);
            line = w;
        } else {
            line = test;
        }
    }
    if (line) lines.push(line);
    return lines;
}

function fitLines(ctx, text, font, maxWidth, maxLines) {
    ctx.font = font;
    let lines = wrap(ctx, text, maxWidth);
    if (lines.length > maxLines) {
        lines = lines.slice(0, maxLines);
        lines[maxLines - 1] = lines[maxLines - 1].replace(/\s+\S*$/, '') + '…';
    }
    return lines;
}

export async function renderStoryImage(a) {
    try {
        await document.fonts?.ready;
    } catch {
        /* fonts optional */
    }
    const c = document.createElement('canvas');
    c.width = W;
    c.height = H;
    const ctx = c.getContext('2d');

    // Background + glow
    ctx.fillStyle = '#0A0A0A';
    ctx.fillRect(0, 0, W, H);
    const g = ctx.createRadialGradient(W * 0.8, H * 0.25, 40, W * 0.8, H * 0.25, 700);
    g.addColorStop(0, 'rgba(226,101,15,0.45)');
    g.addColorStop(1, 'rgba(226,101,15,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);

    // Globe rings
    ctx.strokeStyle = 'rgba(255,138,61,0.35)';
    ctx.lineWidth = 2;
    for (const r of [260, 190, 120]) {
        ctx.beginPath();
        ctx.arc(W * 0.8, H * 0.25, r, 0, Math.PI * 2);
        ctx.stroke();
    }

    const pad = 90;
    // Brand
    ctx.fillStyle = '#EDEAE6';
    ctx.font = '600 44px Fraunces, Georgia, serif';
    ctx.fillText('NewsNetWide', pad, 130);

    // Country + topic
    ctx.font = '500 30px "JetBrains Mono", monospace';
    ctx.fillStyle = a.category_color || '#FF8A3D';
    const label = `${(a.country_name || '').toUpperCase()}  ·  ${(a.category_name || '').toUpperCase()}`;
    ctx.fillText(label, pad, 520);

    // Headline
    ctx.fillStyle = '#EDEAE6';
    const titleLines = fitLines(ctx, a.title, '600 72px Fraunces, Georgia, serif', W - pad * 2, 6);
    titleLines.forEach((l, i) => ctx.fillText(l, pad, 620 + i * 86));

    // Source + date
    const y = 620 + titleLines.length * 86 + 40;
    ctx.font = '400 32px Inter, Helvetica, sans-serif';
    ctx.fillStyle = '#A3A3A3';
    const when = a.source_published_at ? new Date(a.source_published_at.replace(' ', 'T') + (a.source_published_at.endsWith('Z') ? '' : 'Z')) : null;
    const dateStr = when && !isNaN(when) ? when.toLocaleDateString(lang === 'en' ? 'en-GB' : lang, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
    ctx.fillText(`${t('share.source')}: ${a.source_name || ''}${dateStr ? '  ·  ' + dateStr : ''}`, pad, y);

    // Footer
    ctx.fillStyle = '#E2650F';
    ctx.fillRect(pad, H - 190, W - pad * 2, 3);
    ctx.fillStyle = '#EDEAE6';
    ctx.font = '600 40px Inter, Helvetica, sans-serif';
    fitLines(ctx, t('share.tagline'), '600 40px Inter, Helvetica, sans-serif', W - pad * 2, 1).forEach((l) => ctx.fillText(l, pad, H - 120));
    ctx.fillStyle = '#FF8A3D';
    ctx.font = '600 38px "JetBrains Mono", monospace';
    ctx.fillText('newsnetwide.com', pad, H - 65);
    ctx.fillStyle = '#6E6E6E';
    ctx.font = '400 22px Inter, Helvetica, sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(t('share.notAdvice'), W - pad, H - 65);

    return new Promise((resolve) => c.toBlob(resolve, 'image/png'));
}

export async function shareStory(a) {
    if (!a) return;
    const blob = await renderStoryImage(a);
    const file = new File([blob], 'newsnetwide-story.png', { type: 'image/png' });
    const text = `${a.title}, via newsnetwide.com`;
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
            await navigator.share({ files: [file], text, url: 'https://newsnetwide.com' });
            return;
        } catch {
            /* cancelled: fall through to download */
        }
    }
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'newsnetwide-story.png';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(link.href), 5000);
}
